
// tabs/TaxesTab.jsx - Taxes tab
import React, { useState } from 'react';
import { Box, Grid, Stack, TextInput, Group, Text, NumberInput, Radio, Checkbox, Button } from '@mantine/core';

const TaxesTab = ({ quoteData, setQuoteData }) => {
  const [taxesData, setTaxesData] = useState({
    docSubTotalTaxBy: 'doc', // 'doc' or 'line'
    taxCodes: {
      taxA: { code: 'STT', percentage: 0.0000, total: 0.00, addToTotal: true, calcBy: 'byPercent' },
      taxB: { code: 'CDT', percentage: 0.0000, total: 0.00, addToTotal: true, calcBy: 'byPercent' }
    },
    taxOnTax: false,
    grandTotal: 0.00
  });

  const handleDocTaxByChange = (value) => {
    setTaxesData(prev => ({ ...prev, docSubTotalTaxBy: value }));
  };

  const handleTaxCodeChange = (tax, field, value) => {
    setTaxesData(prev => ({
      ...prev,
      taxCodes: {
        ...prev.taxCodes,
        [tax]: { ...prev.taxCodes[tax], [field]: value }
      }
    }));
  };

  const handleChange = (field, value) => {
    setTaxesData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box p="md">
      <Grid gutter="md">
        <Grid.Col span={12}>
          {/* Doc SubTotal Tax By */}
          <Group spacing="md" mb="lg">
            <Text size="sm" fw={500}>Doc SubTotal Tax By:</Text>
            <Radio.Group value={taxesData.docSubTotalTaxBy} onChange={handleDocTaxByChange}>
              <Group spacing="md">
                <Radio value="doc" label="Doc" />
                <Radio value="line" label="Line" />
              </Group>
            </Radio.Group>
          </Group>

          {/* Tax Table */}
          <Grid gutter="md">
            <Grid.Col span={2}>
              <Text size="sm" fw={500} ta="center">Tax Codes</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size="sm" fw={500} ta="center">Tax %</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size="sm" fw={500} ta="center">Total Tax</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size="sm" fw={500} ta="center">Add to Total</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Text size="sm" fw={500} ta="center">Calc Tax By</Text>
            </Grid.Col>
          </Grid>

          {/* Tax A */}
          <Grid gutter="md" align="center" mb="sm">
            <Grid.Col span={1}>
              <Text size="sm" fw={500}>Tax A:</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                value={taxesData.taxCodes.taxA.code}
                variant="filled"
                size="sm"
                styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                onChange={(e) => handleTaxCodeChange('taxA', 'code', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <NumberInput
                value={taxesData.taxCodes.taxA.percentage}
                variant="filled"
                size="sm"
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={4}
                fixedDecimalScale
                onChange={(value) => handleTaxCodeChange('taxA', 'percentage', value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <NumberInput
                value={taxesData.taxCodes.taxA.total}
                variant="filled"
                size="sm"
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleTaxCodeChange('taxA', 'total', value)}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Checkbox
                checked={taxesData.taxCodes.taxA.addToTotal}
                onChange={(e) => handleTaxCodeChange('taxA', 'addToTotal', e.currentTarget.checked)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Radio.Group
                value={taxesData.taxCodes.taxA.calcBy}
                onChange={(value) => handleTaxCodeChange('taxA', 'calcBy', value)}
              >
                <Group spacing="xs">
                  <Radio value="byDollar" label="By $ $" />
                  <Radio value="byPercent" label="By %" />
                </Group>
              </Radio.Group>
            </Grid.Col>
          </Grid>

          {/* Tax B */}
          <Grid gutter="md" align="center" mb="sm">
            <Grid.Col span={1}>
              <Text size="sm" fw={500}>Tax B:</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                value={taxesData.taxCodes.taxB.code}
                variant="filled"
                size="sm"
                styles={{ input: { fontWeight: 'bold', color: 'blue' } }}
                onChange={(e) => handleTaxCodeChange('taxB', 'code', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <NumberInput
                value={taxesData.taxCodes.taxB.percentage}
                variant="filled"
                size="sm"
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={4}
                fixedDecimalScale
                onChange={(value) => handleTaxCodeChange('taxB', 'percentage', value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <NumberInput
                value={taxesData.taxCodes.taxB.total}
                variant="filled"
                size="sm"
                styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
                decimalScale={2}
                fixedDecimalScale
                onChange={(value) => handleTaxCodeChange('taxB', 'total', value)}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Checkbox
                checked={taxesData.taxCodes.taxB.addToTotal}
                onChange={(e) => handleTaxCodeChange('taxB', 'addToTotal', e.currentTarget.checked)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Radio.Group
                value={taxesData.taxCodes.taxB.calcBy}
                onChange={(value) => handleTaxCodeChange('taxB', 'calcBy', value)}
              >
                <Group spacing="xs">
                  <Radio value="byDollar" label="By $ $" />
                  <Radio value="byPercent" label="By %" />
                </Group>
              </Radio.Group>
            </Grid.Col>
          </Grid>

          {/* Tax On Tax */}
          <Group spacing="xs" align="center" mb="md">
            <Text size="sm" fw={500}>Tax On Tax:</Text>
            <Checkbox
              checked={taxesData.taxOnTax}
              onChange={(e) => handleChange('taxOnTax', e.currentTarget.checked)}
            />
          </Group>

          {/* Total */}
          <Group spacing="xs" align="flex-end" justify="center" mt="lg">
            <Text size="sm" fw={500}>Total:</Text>
            <NumberInput
              value={taxesData.grandTotal}
              variant="filled"
              size="sm"
              w={100}
              styles={{ input: { textAlign: 'right', fontWeight: 'bold', color: 'blue' } }}
              decimalScale={2}
              fixedDecimalScale
              onChange={(value) => handleChange('grandTotal', value)}
            />
          </Group>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default TaxesTab;